import {
    AUTH_ENDPOINT,
    AUTH_MFA_ENDPOINT,
    AUTH_MFA_RESEND_ENDPOINT,
    AUTH_PASSKEY_ENDPOINT,
    AUTH_WAF_ENDPOINT,
    REFRESH_ENDPOINT,
    LOGOUT_ENDPOINT,
    STOREFRONT_LOGOUT_URL,
    USERS_ENDPOINT,
    SIGNUP_WAF_ENDPOINT,
    PARTNER_ONBOARDING_ENDPOINT,
    PASSKEY_REGISTER_BEGIN,
    PASSKEY_REGISTER_COMPLETE,
    PASSKEY_AUTHENTICATE_BEGIN,
} from '@perpay-web/authentication/constants/urls';
import { SHOP_LAST_PAGE_COOKIE } from '@perpay-web/constants/cookies';
import { HTTPMethod } from '@perpay-web/constants/httpMethods';
import { HOST, PROD_HOST } from '@perpay-web/constants/urls';
import { expireCookie } from '@perpay-web/utils/cookieUtils';
import { allSettled } from '@perpay-web/utils/promiseUtils';
import { jsonFetch } from '@perpay-web/utils/requestUtils';
import { clearSessionStorage } from '@perpay-web/utils/sessionStorageUtils';
import {
    decodeTokens,
    expireRefreshCookie,
    removeToken,
    storeToken,
} from '@perpay-web/utils/tokenUtils';

const timeout = (delayMs) =>
    new Promise((resolve) => {
        setTimeout(resolve, delayMs);
    });

function getAuthorizationHeader(accessToken) {
    return {
        AUTHORIZATION: `Bearer ${accessToken}`,
    };
}

export function internalAuthenticatedJsonFetch(
    accessToken,
    url,
    body,
    method = HTTPMethod.get,
    headers = {},
) {
    return jsonFetch(
        url,
        body,
        method,
        {
            ...getAuthorizationHeader(accessToken),
            ...headers,
        },
        'include',
    );
}

/**
 * `requestAuthenticate`, `requestRefresh`, `requestSignup` and `requestLogout` are responsible
 * for fetching and decoding the tokens returned by the authentication requests.
 * These methods do not manage internal state for the service.
 */

export const requestAuthenticate = (credentials) =>
    jsonFetch(AUTH_ENDPOINT, credentials, HTTPMethod.post).then((tokens) => {
        const decodedTokens = decodeTokens(tokens);
        // don't store mfa token in local storage.
        if (decodedTokens.mfa) {
            return decodedTokens.mfa;
        }
        storeToken(decodedTokens);
        return decodedTokens;
    });

export const requestAuthenticateWaf = (credentials) =>
    window.AwsWafIntegration.getToken().then((token) =>
        jsonFetch(
            AUTH_WAF_ENDPOINT,
            credentials,
            HTTPMethod.post,
            token
                ? {
                      'x-aws-waf-token': token,
                  }
                : {},
        ).then((tokens) => {
            const decodedTokens = decodeTokens(tokens);
            // don't store mfa token in local storage.
            if (decodedTokens.mfa) {
                return decodedTokens.mfa;
            }
            storeToken(decodedTokens);
            return decodedTokens;
        }),
    );

export const requestAuthenticateMFA = (payload) =>
    jsonFetch(AUTH_MFA_ENDPOINT, payload, HTTPMethod.post).then((tokens) => {
        const decodedTokens = decodeTokens(tokens);
        storeToken(decodedTokens);
        return decodedTokens;
    });

export const requestResendMFA = (payload) =>
    jsonFetch(AUTH_MFA_RESEND_ENDPOINT, payload, HTTPMethod.post).then(
        (tokens) => {
            const decodedTokens = decodeTokens(tokens);
            return decodedTokens.mfa;
        },
    );

export const requestPasskeyAuthenticateBegin = (payload) =>
    jsonFetch(
        PASSKEY_AUTHENTICATE_BEGIN,
        payload,
        HTTPMethod.post,
        {},
        'include',
    );

export const requestAuthenticatePasskey = (credentials) =>
    jsonFetch(
        AUTH_PASSKEY_ENDPOINT,
        credentials,
        HTTPMethod.post,
        {},
        'include',
    ).then((tokens) => {
        const decodedTokens = decodeTokens(tokens);
        storeToken(decodedTokens);
        return decodedTokens;
    });

export const requestRefresh = (refreshToken) => {
    const body = { refresh: refreshToken };

    return jsonFetch(
        REFRESH_ENDPOINT,
        body,
        HTTPMethod.post,
        {},
        'include',
    ).then((tokens) => {
        const decodedTokens = decodeTokens({
            access: tokens.access,
            refresh: refreshToken,
        });
        storeToken(decodedTokens);
        return decodedTokens;
    });
};

export const requestSignup = (accountData) =>
    jsonFetch(USERS_ENDPOINT, accountData, HTTPMethod.post).then((results) => {
        const { tokens } = results;
        storeToken(decodeTokens(tokens));
        return results;
    });

export const requestSignupWaf = (accountData) =>
    window.AwsWafIntegration.getToken().then((token) =>
        jsonFetch(
            SIGNUP_WAF_ENDPOINT,
            accountData,
            HTTPMethod.post,
            token
                ? {
                      'x-aws-waf-token': token,
                  }
                : {},
        ).then((results) => {
            const { tokens } = results;
            storeToken(decodeTokens(tokens));
            return results;
        }),
    );

export const requestPartnerHostedSignup = (userInfo) =>
    jsonFetch(PARTNER_ONBOARDING_ENDPOINT, userInfo, HTTPMethod.post).then(
        (results) => {
            const { tokens } = results;
            storeToken(decodeTokens(tokens));
            return results;
        },
    );

export const requestPasskeyRegisterBegin = (accessToken) =>
    internalAuthenticatedJsonFetch(
        accessToken,
        PASSKEY_REGISTER_BEGIN,
        {},
        HTTPMethod.post,
    );

export const requestPasskeyRegisterComplete = (payload, accessToken) =>
    internalAuthenticatedJsonFetch(
        accessToken,
        PASSKEY_REGISTER_COMPLETE,
        payload,
        HTTPMethod.post,
    );

const STOREFRONT_LOGOUT_TIMEOUT_MS = 3000;

const requestStorefrontLogout = () =>
    Promise.race([
        fetch(STOREFRONT_LOGOUT_URL, {
            mode: 'no-cors', // do not require the server to send CORS headers
            credentials: 'include', // allow the server to set cookies
        }),
        timeout(STOREFRONT_LOGOUT_TIMEOUT_MS),
    ]);

// We pass the prod host since the cookie is stored on the root domain .perpay.com
const expireShopLastPageCookie = () =>
    expireCookie(PROD_HOST, SHOP_LAST_PAGE_COOKIE);

export const requestLogout = (refreshToken) =>
    allSettled([
        jsonFetch(
            LOGOUT_ENDPOINT,
            { refresh: refreshToken },
            HTTPMethod.post,
            {},
            'include',
        ),
        requestStorefrontLogout(),
    ]).then(() => {
        expireRefreshCookie(HOST);
        expireShopLastPageCookie();
        removeToken();
        clearSessionStorage();
    });
